// File: src/emura-tech/pages/OurServices.jsx
import React from "react";
import { motion } from "framer-motion";
import { Users } from "lucide-react";

const services = [
    {
        title: "Performance Analytics",
        description:
            "Utilize our cutting-edge analytics tools to gain deep insights into your business performance.",
        icon: "📊",
    },
    {
        title: "Technology Integration",
        description:
            "We specialize in integrating advanced technologies into your existing systems, ensuring smooth transitions and minimal disruptions.",
        icon: "🔌",
    },
    {
        title: "Software Development",
        description:
            "Our team of experienced developers creates custom software solutions tailored to your specific business requirements.",
        icon: "💻",
    },
    {
        title: "Strategic Consulting",
        description:
            "Our expert consultants work with you to develop tailored strategies that drive growth and streamline operations.",
        icon: "📈",
    },
];

export default function OurServices() {
    return (
        <>
        <section className="py-20 px-4 md:px-10 font-sans">
            <div className="bg-black text-white">
                {/* Hero Section */}
                <div className="relative min-h-screen">
                    <div className="absolute inset-0">
                        <img
                            src="https://images.unsplash.com/photo-1573164713988-8665fc963095?auto=format&fit=crop&w=1350&q=80"
                            alt="Solutions Background"
                            className="w-full h-full object-cover opacity-70"
                        />
                        <div className="absolute inset-0 bg-black opacity-50" />
                    </div>

                    <div className="relative z-10 flex flex-col justify-center items-start h-screen max-w-6xl mx-auto px-6">
                        <motion.h1
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8 }}
                            className="text-5xl font-bold leading-tight mb-4"
                        >
                            Innovative <span className="text-green-400">business</span><br />solutions in <span className="text-blue-400">AFRICA</span>.
                        </motion.h1>
                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.5, duration: 0.8 }}
                            className="text-lg text-gray-300 max-w-xl"
                        >
                            At Emura Group, we provide the tools and expertise to help your business thrive in today’s competitive landscape.
                        </motion.p>

                        <div className="mt-8 flex space-x-4">
                            <button className="bg-white text-black px-6 py-3 rounded-full font-semibold hover:bg-gray-200">
                                Let’s talk
                            </button>
                            <button className="bg-black border border-white text-white px-6 py-3 rounded-full font-semibold hover:bg-white hover:text-black">
                                Our solutions →
                            </button>
                        </div>

                        <div className="mt-10 h-3 w-full max-w-md bg-gradient-to-r from-yellow-300 via-green-400 to-blue-500 rounded-full" />
                    </div>
                </div>
            </div>
        </section>
        <section className="bg-white text-black py-20 px-4 md:px-10 font-sans">
            <div className="max-w-7xl mx-auto">
                <h2 className="text-4xl font-bold text-center mb-6">
                    Solutions designed to <br /> meet the unique needs <br /> of your business.
                </h2>

                <div className="grid md:grid-cols-3 gap-6 mt-12">
                    {services.slice(0, 2).map((service, idx) => (
                        <motion.div
                            key={idx}
                            whileHover={{ scale: 1.02 }}
                            className="bg-black text-white rounded-2xl p-6 space-y-3 shadow-xl"
                        >
                            <div className="text-3xl">{service.icon}</div>
                            <h3 className="text-xl font-semibold">{service.title}</h3>
                            <p className="text-gray-300 text-sm">{service.description}</p>
                        </motion.div>
                    ))}

                    <motion.div
                        whileHover={{ scale: 1.02 }}
                        className="bg-black text-white rounded-2xl p-6 space-y-4 shadow-xl flex flex-col justify-between"
                    >
                        <p className="text-lg font-semibold">
                            From user-friendly applications to complex systems, we deliver high-quality, scalable software that enhances efficiency and drives innovation.
                        </p>
                        <a href="/our-solutions" className="text-blue-400 font-semibold inline-flex items-center space-x-1">
                            <span>Our solutions</span>
                            <span>→</span>
                        </a>
                        <div className="mt-4">
                            <img
                                src="https://upload.wikimedia.org/wikipedia/commons/1/1e/Graph_icon.svg"
                                alt="Graph"
                                className="w-full max-h-28 object-contain"
                            />
                        </div>
                    </motion.div>

                    {services.slice(2).map((service, idx) => (
                        <motion.div
                            key={idx}
                            whileHover={{ scale: 1.02 }}
                            className="bg-black text-white rounded-2xl p-6 space-y-3 shadow-xl"
                        >
                            <div className="text-3xl">{service.icon}</div>
                            <h3 className="text-xl font-semibold">{service.title}</h3>
                            <p className="text-gray-300 text-sm">{service.description}</p>
                        </motion.div>
                    ))}

                    <motion.div
                        whileHover={{ scale: 1.02 }}
                        className="bg-black text-white rounded-2xl p-6 flex items-center space-x-4 shadow-xl"
                    >
                        <div className="flex -space-x-2 overflow-hidden">
                            <img className="inline-block h-10 w-10 rounded-full ring-2 ring-white" src="https://randomuser.me/api/portraits/women/44.jpg" alt="" />
                            <img className="inline-block h-10 w-10 rounded-full ring-2 ring-white" src="https://randomuser.me/api/portraits/men/45.jpg" alt="" />
                            <img className="inline-block h-10 w-10 rounded-full ring-2 ring-white" src="https://randomuser.me/api/portraits/women/46.jpg" alt="" />
                        </div>
                        <div>
                            <p className="font-bold text-green-400">1M+ people trusted</p>
                            <p className="text-sm text-gray-300">our services</p>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section></>
    );
}
